/**
 * Pure (React-free) timing, caption and formatting logic for ScenarioExplainerClip.
 * Deterministic: depends only on validated props + fps.
 */
import { MAX_DURATION_SECONDS } from "../lib/renderLimits.js";
import { normalizeCaptionWords } from "../lib/captionLogic.js";

export const SEGMENT_SECONDS = {
  hook: 1.6,
  setup: 3.4,
  chart: 4.2,
  cta: 3.0,
  endMin: 3.0,
  endMax: 6.0,
  beatMin: 2.8,
  beatMax: 6.0,
  beatFloorWhenShrinking: 1.5,
};

/** Voiceover must finish this long before the end card starts. */
const AUDIO_TAIL_SECONDS = 0.4;

function wordCount(text) {
  return String(text || "").split(/\s+/).filter(Boolean).length;
}

function clamp(value, lo, hi) {
  return Math.min(hi, Math.max(lo, value));
}

/** Reading-time based beat length (seconds). */
export function beatSeconds(beat) {
  const words = wordCount(beat.text);
  const base = 1.8 + words * 0.26 + (beat.emphasis ? 0.3 : 0);
  return clamp(base, SEGMENT_SECONDS.beatMin, SEGMENT_SECONDS.beatMax);
}

/** End card stays long enough to read the disclaimer (always >= 3 s, so >= 2 s visible). */
export function endCardSeconds(disclaimer) {
  return clamp(2.0 + wordCount(disclaimer) * 0.12, SEGMENT_SECONDS.endMin, SEGMENT_SECONDS.endMax);
}

/**
 * Last spoken moment of the voiceover in seconds (0 when none).
 * `probedAudioSeconds` is the measured file length, when known.
 */
export function resolveVoiceoverSeconds(voiceover, probedAudioSeconds = 0) {
  if (!voiceover) return 0;
  const words = normalizeCaptionWords(voiceover.words);
  const lastWordEnd = words.length ? Math.max(...words.map((w) => w.endMs)) / 1000 : 0;
  const declared = Number(voiceover.durationSeconds) || 0;
  return Math.max(lastWordEnd, declared, Number(probedAudioSeconds) || 0);
}

/**
 * Build the segment timeline in frames.
 * hook → setup → chart → beats → cta → end. Beats stretch to cover voiceover audio
 * and shrink (never below a floor) to respect MAX_DURATION_SECONDS; the end card is
 * never shortened.
 */
export function buildTimeline(props, fps, voiceoverSeconds = 0) {
  const beatLens = props.beats.map(beatSeconds);
  const fixedBefore = SEGMENT_SECONDS.hook + SEGMENT_SECONDS.setup + SEGMENT_SECONDS.chart;
  const endLen = endCardSeconds(props.disclaimer);
  const beatsTotal = beatLens.reduce((a, b) => a + b, 0);

  // Stretch beats so voiceover finishes before the end card.
  const neededBeforeEnd = voiceoverSeconds + AUDIO_TAIL_SECONDS;
  const currentBeforeEnd = fixedBefore + beatsTotal + SEGMENT_SECONDS.cta;
  let scale = 1;
  if (neededBeforeEnd > currentBeforeEnd && beatsTotal > 0) {
    scale = (beatsTotal + (neededBeforeEnd - currentBeforeEnd)) / beatsTotal;
  }

  // Shrink beats if we would exceed the hard render limit.
  const maxBeats = MAX_DURATION_SECONDS - fixedBefore - SEGMENT_SECONDS.cta - endLen;
  if (beatsTotal * scale > maxBeats) {
    scale = Math.max(maxBeats, SEGMENT_SECONDS.beatFloorWhenShrinking * beatLens.length) / beatsTotal;
  }

  const toFrames = (seconds) => Math.max(1, Math.round(seconds * fps));
  const segments = [];
  let cursor = 0;
  const push = (key, seconds, extra = {}) => {
    const durationInFrames = toFrames(seconds);
    segments.push({ key, from: cursor, durationInFrames, ...extra });
    cursor += durationInFrames;
  };

  push("hook", SEGMENT_SECONDS.hook);
  push("setup", SEGMENT_SECONDS.setup);
  push("chart", SEGMENT_SECONDS.chart);
  beatLens.forEach((len, index) => push("beat", len * scale, { index }));
  push("cta", SEGMENT_SECONDS.cta);
  push("end", endLen);

  return { segments, totalFrames: cursor, fps };
}

export function findSegment(timeline, frame) {
  const { segments } = timeline;
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (frame >= segments[i].from) return segments[i];
  }
  return segments[0];
}

// ── Captions ────────────────────────────────────────────────

/**
 * Caption words for the whole video (ms from video start).
 * Voiceover word timings win; otherwise each beat's text is spread evenly over
 * its segment (the hook / setup / CTA cards already show their text on screen).
 */
export function buildCaptionWords(props, timeline) {
  const spoken = normalizeCaptionWords(props.voiceover?.words);
  if (spoken.length) return spoken.map((w) => ({ ...w, group: null }));

  const { fps } = timeline;
  const words = [];
  const spread = (text, segment, group) => {
    const tokens = String(text).split(/\s+/).filter(Boolean);
    if (!tokens.length) return;
    const startMs = (segment.from / fps) * 1000 + 250;
    const spanMs = (segment.durationInFrames / fps) * 1000 * 0.82;
    const step = spanMs / tokens.length;
    tokens.forEach((token, i) => {
      words.push({
        text: token,
        startMs: Math.round(startMs + i * step),
        endMs: Math.round(startMs + (i + 1) * step),
        group,
      });
    });
  };

  timeline.segments.forEach((segment) => {
    if (segment.key === "beat") spread(props.beats[segment.index].text, segment, `beat-${segment.index}`);
  });
  return words;
}

/**
 * Group words into short caption chunks (max ~2 short lines). Phrases break on
 * group boundaries, sentence punctuation and silences; each phrase is then split
 * into evenly sized chunks so no chunk is left with a single orphan word.
 */
export function buildCaptionChunks(words, { maxChars = 24, maxWords = 5, maxGapMs = 650 } = {}) {
  const phrases = [];
  let phrase = null;
  words.forEach((word, globalIndex) => {
    const prev = phrase?.[phrase.length - 1];
    const startsNew =
      !phrase ||
      word.group !== prev.group ||
      word.startMs - prev.endMs > maxGapMs ||
      /[.!?]$/.test(prev.text);
    if (startsNew) {
      phrase = [];
      phrases.push(phrase);
    }
    phrase.push({ ...word, globalIndex });
  });

  const chunks = [];
  phrases.forEach((items) => {
    const chars = items.reduce((n, w) => n + w.text.length, 0) + items.length - 1;
    const count = Math.max(Math.ceil(items.length / maxWords), Math.ceil(chars / maxChars), 1);
    for (let c = 0; c < count; c += 1) {
      const slice = items.slice(Math.round((c * items.length) / count), Math.round(((c + 1) * items.length) / count));
      if (!slice.length) continue;
      chunks.push({
        group: slice[0].group,
        words: slice,
        startMs: slice[0].startMs,
        endMs: Math.max(...slice.map((w) => w.endMs)),
      });
    }
  });
  return chunks;
}

/** Chunk visible at `ms`, holding briefly after its last word, or null. */
export function resolveCaptionChunk(chunks, ms, holdMs = 500) {
  for (let i = chunks.length - 1; i >= 0; i -= 1) {
    const chunk = chunks[i];
    if (ms >= chunk.startMs) {
      const next = chunks[i + 1];
      const until = Math.min(chunk.endMs + holdMs, next ? next.startMs : Infinity);
      return ms < until ? chunk : null;
    }
  }
  return null;
}

// ── Formatting (deterministic, en-US) ───────────────────────

const MINUS = "−";

function groupDigits(value, decimals) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

function moneyDecimals(abs) {
  return abs >= 1000 || Number.isInteger(abs) ? 0 : 2;
}

export function formatMoney(value, { signed = false, decimals } = {}) {
  const abs = Math.abs(value);
  const body = `$${groupDigits(abs, decimals ?? moneyDecimals(abs))}`;
  if (value < 0) return `${MINUS}${body}`;
  return signed && value > 0 ? `+${body}` : body;
}

export function formatPrice(value) {
  return formatMoney(value, { decimals: 2 });
}

export function formatAmountOrUnlimited(value, { loss = false } = {}) {
  if (value === "Unlimited") return "Unlimited";
  const abs = Math.abs(value);
  return loss ? `${MINUS}${formatMoney(abs)}` : formatMoney(abs);
}

export function formatNetDebitCredit(value) {
  if (value > 0) return { label: "Net debit", value: formatMoney(value) };
  if (value < 0) return { label: "Net credit", value: formatMoney(Math.abs(value)) };
  return { label: "Net cost", value: "$0" };
}

export function formatEmphasis({ value, format }) {
  const abs = Math.abs(value);
  const num = (v, d) => groupDigits(v, d ?? (Number.isInteger(v) ? 0 : 1));
  switch (format) {
    case "signedCurrency":
      return formatMoney(value, { signed: true });
    case "price":
      return formatPrice(value);
    case "percent":
      return `${value < 0 ? MINUS : ""}${num(abs)}%`;
    case "signedPercent":
      return `${value < 0 ? MINUS : value > 0 ? "+" : ""}${num(abs)}%`;
    case "number":
      return `${value < 0 ? MINUS : ""}${num(abs)}`;
    case "signedNumber":
      return `${value < 0 ? MINUS : value > 0 ? "+" : ""}${num(abs)}`;
    case "currency":
    default:
      return formatMoney(value);
  }
}

// ── Payoff helpers ──────────────────────────────────────────

/** Linear interpolation of P&L at `price` along a sorted payoff series. */
export function pnlAtPrice(series, price) {
  if (!series?.length) return 0;
  if (price <= series[0].underlyingPrice) return series[0].pnl;
  const last = series[series.length - 1];
  if (price >= last.underlyingPrice) return last.pnl;
  for (let i = 1; i < series.length; i += 1) {
    const a = series[i - 1];
    const b = series[i];
    if (price <= b.underlyingPrice) {
      const t = (price - a.underlyingPrice) / (b.underlyingPrice - a.underlyingPrice || 1);
      return a.pnl + t * (b.pnl - a.pnl);
    }
  }
  return last.pnl;
}
